import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import api from "@/lib/api"
import type {
  CsClient, CsClientCreate, CsClientUpdate,
  NpsCampaign, NpsCampaignCreate,
  NpsSurvey, NpsSurveyCreate, NpsSurveyPublic, NpsSurveySubmit,
  NpsDashboard,
  ActionPlan, ActionPlanCreate, ActionPlanUpdate,
} from "@/types/customerSuccess"

// ==========================================
// Client Hooks
// ==========================================

interface UseClientsParams {
  page?: number
  perPage?: number
  search?: string
  status?: string | null
}

export function useClients(params: UseClientsParams = {}) {
  return useQuery({
    queryKey: ["cs-clients", params],
    queryFn: async () => {
      const query: Record<string, string | number> = {
        page: params.page || 1,
        per_page: params.perPage || 50,
      }
      if (params.search) query.search = params.search
      if (params.status) query.contract_status = params.status
      const { data } = await api.get<CsClient[]>("/v1/cs/clients", { params: query })
      const arr = Array.isArray(data) ? data : (data as any).items || []
      return { items: arr, total: arr.length, page: 1, pages: 1 }
    },
  })
}

export function useUpdateClient() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ companyId, data: clientData }: { companyId: string; data: CsClientUpdate }) => {
      const { data } = await api.put<CsClient>(`/v1/cs/clients/${companyId}`, clientData)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cs-clients"] })
      qc.invalidateQueries({ queryKey: ["cs-dashboard"] })
    },
  })
}

export function useConvertLeadToClient() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ leadId, data: clientData }: { leadId: string; data?: CsClientCreate }) => {
      const { data } = await api.post<CsClient>(`/v1/cs/clients/from-lead/${leadId}`, clientData || {})
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cs-clients"] })
      qc.invalidateQueries({ queryKey: ["cs-dashboard"] })
      qc.invalidateQueries({ queryKey: ["leads"] })
    },
  })
}

export function useConvertProposalToClient() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ proposalId, data: clientData }: { proposalId: string; data?: CsClientCreate }) => {
      const { data } = await api.post<CsClient>(`/v1/cs/clients/from-proposal/${proposalId}`, clientData || {})
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cs-clients"] })
      qc.invalidateQueries({ queryKey: ["cs-dashboard"] })
    },
  })
}

// ==========================================
// NPS Campaign Hooks
// ==========================================

export function useNpsCampaigns() {
  return useQuery({
    queryKey: ["cs-campaigns"],
    queryFn: async () => {
      const { data } = await api.get<{ items: NpsCampaign[]; total: number }>("/v1/cs/campaigns")
      return data
    },
  })
}

export function useNpsCampaign(id: string | null) {
  return useQuery({
    queryKey: ["cs-campaigns", id],
    queryFn: async () => {
      const { data } = await api.get<NpsCampaign>(`/v1/cs/campaigns/${id}`)
      return data
    },
    enabled: !!id,
  })
}

export function useCreateNpsCampaign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (campaignData: NpsCampaignCreate) => {
      const { data } = await api.post<NpsCampaign>("/v1/cs/campaigns", campaignData)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cs-campaigns"] }),
  })
}

export function useSendNpsCampaign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (campaignId: string) => {
      const { data } = await api.post<NpsCampaign>(`/v1/cs/campaigns/${campaignId}/send`)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cs-campaigns"] })
      qc.invalidateQueries({ queryKey: ["cs-dashboard"] })
    },
  })
}

export function useCancelNpsCampaign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (campaignId: string) => {
      const { data } = await api.put<NpsCampaign>(`/v1/cs/campaigns/${campaignId}/cancel`)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cs-campaigns"] }),
  })
}

// ==========================================
// Survey Hooks
// ==========================================

export function useNpsSurveys(filters: { campaign_id?: string; status?: string; page?: number } = {}) {
  return useQuery({
    queryKey: ["cs-surveys", filters],
    queryFn: async () => {
      const { data } = await api.get<{ items: NpsSurvey[]; total: number }>("/v1/cs/surveys", { params: filters })
      return data
    },
  })
}

export function useNpsSurvey(id: string | null) {
  return useQuery({
    queryKey: ["cs-surveys", id],
    queryFn: async () => {
      const { data } = await api.get<NpsSurvey>(`/v1/cs/surveys/${id}`)
      return data
    },
    enabled: !!id,
  })
}

export function useCreateNpsSurvey() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (surveyData: NpsSurveyCreate) => {
      const { data } = await api.post<NpsSurvey>("/v1/cs/surveys", surveyData)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cs-surveys"] })
      qc.invalidateQueries({ queryKey: ["cs-campaigns"] })
    },
  })
}

// Public (no auth) hooks for respond page
export function usePublicSurvey(token: string | null) {
  return useQuery({
    queryKey: ["cs-respond", token],
    queryFn: async () => {
      const { data } = await api.get<NpsSurveyPublic>(`/v1/cs/respond/${token}`)
      return data
    },
    enabled: !!token,
    retry: false,
  })
}

export function useSubmitSurvey() {
  return useMutation({
    mutationFn: async ({ token, data: submitData }: { token: string; data: NpsSurveySubmit }) => {
      const { data } = await api.post(`/v1/cs/respond/${token}`, submitData)
      return data
    },
  })
}

export function useQuickSubmitSurvey() {
  return useMutation({
    mutationFn: async ({ token, score }: { token: string; score: number }) => {
      const { data } = await api.post(`/v1/cs/respond/${token}/quick`, { score })
      return data
    },
  })
}

// ==========================================
// Dashboard Hook
// ==========================================

export function useNpsDashboard() {
  return useQuery({
    queryKey: ["cs-dashboard"],
    queryFn: async () => {
      const { data } = await api.get<NpsDashboard>("/v1/cs/dashboard")
      return data
    },
    refetchInterval: 60000, // refresh every 60s
  })
}

// ==========================================
// Action Plan Hooks
// ==========================================

export function useActionPlans(filters: { status?: string; page?: number } = {}) {
  return useQuery({
    queryKey: ["cs-action-plans", filters],
    queryFn: async () => {
      const { data } = await api.get<{ items: ActionPlan[]; total: number }>("/v1/cs/action-plans", { params: filters })
      return data
    },
  })
}

export function useCreateActionPlan() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (planData: ActionPlanCreate) => {
      const { data } = await api.post<ActionPlan>("/v1/cs/action-plans", planData)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cs-action-plans"] })
      qc.invalidateQueries({ queryKey: ["cs-dashboard"] })
    },
  })
}

export function useUpdateActionPlan() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ planId, data: planData }: { planId: string; data: ActionPlanUpdate }) => {
      const { data } = await api.put<ActionPlan>(`/v1/cs/action-plans/${planId}`, planData)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cs-action-plans"] })
      qc.invalidateQueries({ queryKey: ["cs-dashboard"] })
    },
  })
}
